import {
  Box,
  Button,
  ButtonGroup,
  Center,
  Heading,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Spinner,
  Stack,
  Tag,
  Text,
  Tooltip,
  type UseDisclosureReturn,
  useBoolean,
  useDisclosure,
} from "@chakra-ui/react";
import { plural, Trans, t } from "@lingui/macro";
import {
  CapacityAllocationRepetitionTypeEnum,
  CapacityAllocationTypeEnum,
  ReopenCapacityAllocationItemDocument,
  type ReopenCapacityAllocationItemMutation,
  type ReopenCapacityAllocationItemMutationVariables,
  useCapacityAllocationUserItemDetailQuery,
} from "@src/__generated__/urql-graphql";
import { PlanningDailyViewItemModel } from "@src/components/modules/resource-planning/timeline/models";
import { useResourcePlanningDailyViewStore } from "@src/components/modules/resource-planning/timeline/ResourcePlanningDayPage";
import { ButtonLink, Link, TaskStatusBadge } from "@src/components/ui-kit";
import { Icon, TrackedTimeIcon } from "@src/components/ui-kit/Icon";
import { ExternalLinkModal } from "@src/components/ui-kit/Modals/external-link-modal";
import { TextEditor } from "@src/components/ui-kit/TextEditor/TextEditor";
import { TimeTrackingButton } from "@src/components/widgets/Modals/ModalCommunication/components";
import { client } from "@src/services/client";
import type { AffectAllocationOccurrences } from "@src/types/planning";
import { can } from "@src/utils/components/permissions";
import { WrapComponent } from "@src/utils/components/WrapComponent";
import { getTodayStart, toApiDate } from "@src/utils/dates";
import { formatDuration } from "@src/utils/formatters";
import { highlightDeadline } from "@src/utils/highlightDeadline";
import { useStore } from "@src/utils/hooks";
import { isTrackingEnabled } from "@src/utils/time-tracking";
import type { AllocationItemProps } from "@src/widgets/AllocationItem/AllocationItem";
import { NoteWithLinks } from "@src/widgets/AllocationPopover/components/note-with-links";
import { format } from "date-fns";
import { kebabCase, uniqBy } from "lodash";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import {
  cloneElement,
  Fragment,
  forwardRef,
  type ReactNode,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

type AllocationPopoverProps = {
  userItem: AllocationItemProps["userItem"];
  children: ReactNode;
};

export type AllocationPopoverRef = Pick<
  UseDisclosureReturn,
  "isOpen" | "onClose" | "onOpen" | "onToggle"
>;

const PropertyLabel = ({ children }: { children: React.ReactNode }) => (
  <Text pr="2" color="grey.500" fontSize="2xs" fontWeight="medium">
    {children}
  </Text>
);

export const AllocationPopover = observer(
  forwardRef<AllocationPopoverRef, AllocationPopoverProps>(
    function AllocationPopover({ children, userItem }, ref) {
      const resourcePlanningStore = useResourcePlanningDailyViewStore();
      const {
        allocationModalStore,
        completeCapacityAllocationModalStore,
        taskDetailModalStore,
        deleteAllocationModalStore,
        authStore,
      } = useStore();
      const { isOpen, onOpen, onClose, onToggle } = useDisclosure();
      const {
        isOpen: isExternalLinkOpen,
        onOpen: onExternalLinkOpen,
        onClose: onExternalLinkClose,
      } = useDisclosure();
      const [externalLinkUrl, setExternalLinkUrl] = useState("");
      const handleNoteLinkClick = useCallback(
        (url: string) => {
          setExternalLinkUrl(url);
          onExternalLinkOpen();
        },
        [onExternalLinkOpen],
      );
      const [{ fetching: fetchingItemDetail, data }] =
        useCapacityAllocationUserItemDetailQuery({
          pause: !isOpen,
          variables: {
            id: userItem.id,
            today: getTodayStart(),
          },
        });
      const capacityAllocationItem = computed(
        () => data?.capacityAllocationItemDetailQuery,
      ).get();
      const caiStats = computed(() => capacityAllocationItem?.stats).get();
      const capacityAllocation = computed(
        () => capacityAllocationItem?.capacityAllocation,
      ).get();
      const budgetItem = computed(
        () => capacityAllocation?.task?.ourWorkBudgetItem,
      ).get();
      const project = computed(() => budgetItem?.project).get();
      const itemsCount = userItem.stats?.items_count ?? 0;
      const hasMultipleAttendees = itemsCount > 1;
      const showTimeOffBadge = computed(() => {
        return (
          capacityAllocationItem?.type === CapacityAllocationTypeEnum.TimeOff ||
          capacityAllocationItem?.type === CapacityAllocationTypeEnum.HomeOffice
        );
      }).get();
      const showMeetingBadge = computed(() => {
        return (
          capacityAllocationItem?.type === CapacityAllocationTypeEnum.Meeting
        );
      }).get();
      const showSpacer = computed(() => {
        return (
          !!capacityAllocation?.task && !showTimeOffBadge && !showMeetingBadge
        );
      });

      useImperativeHandle(
        ref,
        () => ({
          isOpen,
          onClose,
          onOpen,
          onToggle,
        }),
        [],
      );

      const isAuthUser = capacityAllocationItem?.user.id === authStore.user?.id;

      const { otherAssignees, assignees } = useMemo(() => {
        const allAssignees = uniqBy(
          capacityAllocationItem?.userItems,
          "user.id",
        );
        return {
          otherAssignees: allAssignees.filter(
            (assigneeItem) =>
              capacityAllocationItem?.user?.id !== assigneeItem?.user?.id,
          ),
          assignees: allAssignees,
        };
      }, [capacityAllocationItem?.userItems, capacityAllocationItem?.user?.id]);

      const onEdit = (affectOccurrences: AffectAllocationOccurrences) => {
        if (!capacityAllocationItem) return;
        allocationModalStore.modalState.onOpen({
          affectOccurrences,
          formData: {
            id:
              affectOccurrences === "allOccurrences"
                ? capacityAllocation?.id
                : capacityAllocationItem.id,
            item: capacityAllocationItem,
            assignees,
            tracked_for_item:
              capacityAllocationItem.userStats?.tracked_time_for_item,
          },
          onSubmit: () =>
            resourcePlanningStore.fetchDailyView({
              backgroundUpdate: false,
              loadForAllUsers: true,
            }),
        });
      };

      const onDuplicate = (affectOccurrences: AffectAllocationOccurrences) => {
        if (!capacityAllocationItem) return;
        allocationModalStore.modalState.onOpen({
          affectOccurrences,
          formData: {
            item: capacityAllocationItem,
            assignees,
            tracked_for_item:
              capacityAllocationItem?.userStats?.tracked_time_for_item,
          },
          onSubmit: () =>
            resourcePlanningStore.fetchDailyView({
              backgroundUpdate: false,
              loadForAllUsers: true,
            }),
        });
      };

      const onDelete = (affectOccurrences: AffectAllocationOccurrences) => {
        if (!capacityAllocation || !capacityAllocationItem) return;
        deleteAllocationModalStore.modalState.onOpen({
          id: capacityAllocation.id,
          type: capacityAllocationItem.type,
          itemId: capacityAllocationItem?.id,
          deleteFromDate: capacityAllocationItem?.date,
          itemCount: itemsCount,
          affectOccurrences: affectOccurrences,
          onSubmit: (deleteFromDate) => {
            switch (affectOccurrences) {
              case "allOccurrences":
                resourcePlanningStore.locallyDeleteCapacityAllocation(
                  capacityAllocation.id,
                  deleteFromDate ? toApiDate(deleteFromDate) : undefined,
                );
                break;
              case "thisDay":
                resourcePlanningStore.locallyDeleteCapacityAllocation(
                  capacityAllocation.id,
                  deleteFromDate ? toApiDate(deleteFromDate) : undefined,
                  false,
                );
                break;
              default:
                resourcePlanningStore.locallyDeleteAllocationItem(
                  capacityAllocationItem.id,
                );
                break;
            }
            resourcePlanningStore.syncData();
          },
        });
      };

      const onComplete = async () => {
        if (
          !capacityAllocationItem ||
          !capacityAllocation ||
          !capacityAllocationItem
        )
          return;
        completeCapacityAllocationModalStore.modalState.onOpen({
          item: {
            id: capacityAllocationItem.id,
            task_id: capacityAllocation.task?.id,
            task_name: capacityAllocation.task?.name,
            budget_item_title:
              capacityAllocation.task?.ourWorkBudgetItem?.title ?? "",
            project_code:
              capacityAllocation.task?.ourWorkBudgetItem?.project.code ?? "",
            date: capacityAllocationItem.date,
            note: capacityAllocationItem.note,
            item_id: capacityAllocationItem.id,
            user_id: capacityAllocationItem.user.id,
            total_time: capacityAllocationItem.total_time,
            time_tracking_work_type_id:
              capacityAllocationItem.time_tracking_work_type_id,
          },
          resourcePlanningStore,
          mode: "complete",
        });
      };

      const onOpenTask = () => {
        if (!capacityAllocation?.task?.id) return;
        taskDetailModalStore.disclosureState.onOpen({
          id: capacityAllocation.task.id,
        });
      };

      const [isReopenButtonLoading, setIsReopenButtonLoading] =
        useBoolean(false);
      const onReopen = async () => {
        setIsReopenButtonLoading.on();
        const res = await client.mutation<
          ReopenCapacityAllocationItemMutation,
          ReopenCapacityAllocationItemMutationVariables
        >(ReopenCapacityAllocationItemDocument, {
          input: { id: capacityAllocationItem!.id, completed: false },
        });

        if (res.data?.reopenCapacityAllocationItem) {
          resourcePlanningStore.appStore.UIStore.toast({
            title: t`Capacity allocation has been reopened`,
            status: "success",
          });
          completeCapacityAllocationModalStore.locallyToggleCompleted(
            res.data.reopenCapacityAllocationItem,
            undefined,
            resourcePlanningStore,
          );
        }
        setIsReopenButtonLoading.off();
      };

      const onTimeTrackingChange = (running: boolean) => {
        if (!capacityAllocationItem) return;
        resourcePlanningStore
          .findUserItem({
            id: capacityAllocationItem.id,
            userId: capacityAllocationItem.user.id,
            date: capacityAllocationItem.date,
          })
          ?.setHasRunningTimer(running);
      };

      const buildImportedAllocationModel = () => {
        if (!capacityAllocationItem) return undefined;
        return new PlanningDailyViewItemModel(
          {
            ...capacityAllocationItem,
            date: toApiDate(capacityAllocationItem.date),
            stats: {
              ...caiStats,
              capacity_allocation_id: capacityAllocation?.id ?? "",
              capacity_allocation_item_id: capacityAllocationItem.id,
              repetition:
                capacityAllocation?.repetition ??
                CapacityAllocationRepetitionTypeEnum.Once,
              items_count: itemsCount,
              tracking_enabled_from: caiStats?.tracking_enabled_from
                ? toApiDate(caiStats?.tracking_enabled_from)
                : undefined,
              tracking_enabled_to: caiStats?.tracking_enabled_to
                ? toApiDate(caiStats?.tracking_enabled_to)
                : undefined,
            },
          },
          capacityAllocationItem.user,
        );
      };

      const handleAcceptImportedAllocation = () => {
        const model = buildImportedAllocationModel();
        if (!model) return;
        resourcePlanningStore.handleEdit(model, "allOccurrences");
      };

      const handleAcceptImportedAllocationForUser = () => {
        const model = buildImportedAllocationModel();
        if (!model) return;
        resourcePlanningStore.acceptImportedAllocationForUser(model);
      };

      if (!isOpen)
        return (
          <>
            {children}
            <ExternalLinkModal
              url={externalLinkUrl}
              isOpen={isExternalLinkOpen}
              onClose={onExternalLinkClose}
            />
          </>
        );

      return (
        <>
          <Popover
            isLazy
            isOpen={isOpen}
            onClose={onClose}
            onOpen={onOpen}
            placement="right"
          >
            <PopoverTrigger>{children}</PopoverTrigger>
            <Portal>
              <PopoverContent
                pos="relative"
                zIndex="50"
                w="max-content"
                minW="400px"
              >
                <PopoverArrow />
                {fetchingItemDetail ? (
                  <Center h="310px">
                    <Spinner color="teal.500" />
                  </Center>
                ) : (
                  <Fragment>
                    <PopoverHeader pb="0" borderBottom="none">
                      {capacityAllocationItem?.type ===
                      CapacityAllocationTypeEnum.Imported ? (
                        <HStack justify="space-between">
                          <ButtonGroup variant="subtle">
                            {hasMultipleAttendees ? (
                              <Menu>
                                <Tooltip label={t`Accept imported allocation`}>
                                  <MenuButton
                                    as={IconButton}
                                    aria-label={t`Accept imported allocation`}
                                    colorScheme="green"
                                    icon={<Icon name="check" />}
                                    isLoading={resourcePlanningStore.deletingAllocationItemId.has(
                                      capacityAllocation?.id ?? "",
                                    )}
                                  />
                                </Tooltip>
                                <MenuList>
                                  <MenuItem
                                    onClick={handleAcceptImportedAllocation}
                                  >
                                    <Trans>Accept for all</Trans>
                                  </MenuItem>
                                  <MenuItem
                                    onClick={
                                      handleAcceptImportedAllocationForUser
                                    }
                                  >
                                    <Trans>Accept for this user</Trans>
                                  </MenuItem>
                                </MenuList>
                              </Menu>
                            ) : (
                              <Tooltip label={t`Accept imported allocation`}>
                                <IconButton
                                  aria-label={t`Accept imported allocation`}
                                  colorScheme="green"
                                  icon={<Icon name="check" />}
                                  isLoading={resourcePlanningStore.deletingAllocationItemId.has(
                                    capacityAllocation?.id ?? "",
                                  )}
                                  onClick={handleAcceptImportedAllocation}
                                />
                              </Tooltip>
                            )}
                            {hasMultipleAttendees ? (
                              <Menu>
                                <Tooltip label={t`Decline imported allocation`}>
                                  <MenuButton
                                    as={IconButton}
                                    aria-label={t`Decline imported allocation`}
                                    colorScheme="red"
                                    icon={<Icon name="x-close" />}
                                    isLoading={resourcePlanningStore.deletingAllocationItemId.has(
                                      capacityAllocation?.id ?? "",
                                    )}
                                  />
                                </Tooltip>
                                <MenuList>
                                  <MenuItem
                                    onClick={() => {
                                      resourcePlanningStore.declineImportedAllocation(
                                        capacityAllocation!.id,
                                      );
                                    }}
                                  >
                                    <Trans>Decline for all</Trans>
                                  </MenuItem>
                                  <MenuItem
                                    onClick={() => {
                                      if (!capacityAllocationItem) return;
                                      resourcePlanningStore.declineImportedAllocationForUser(
                                        capacityAllocation!.id,
                                        capacityAllocationItem.user.id,
                                      );
                                    }}
                                  >
                                    <Trans>Decline for this user</Trans>
                                  </MenuItem>
                                </MenuList>
                              </Menu>
                            ) : (
                              <Tooltip label={t`Decline imported allocation`}>
                                <IconButton
                                  aria-label={t`Decline imported allocation`}
                                  colorScheme="red"
                                  icon={<Icon name="x-close" />}
                                  isLoading={resourcePlanningStore.deletingAllocationItemId.has(
                                    capacityAllocation?.id ?? "",
                                  )}
                                  onClick={() => {
                                    resourcePlanningStore.declineImportedAllocation(
                                      capacityAllocation!.id,
                                    );
                                  }}
                                />
                              </Tooltip>
                            )}
                          </ButtonGroup>
                          <Tooltip label={t`Close`}>
                            <IconButton
                              aria-label={t`Close`}
                              colorScheme="grey"
                              icon={<Icon name="x-close" />}
                              onClick={onClose}
                              variant="ghost"
                            />
                          </Tooltip>
                        </HStack>
                      ) : (
                        <HStack justify="space-between">
                          {!!capacityAllocation?.task && (
                            <TaskStatusBadge
                              h="8"
                              px="4"
                              rounded="md"
                              name={capacityAllocation.task.status.name}
                              foreground_color={
                                capacityAllocation.task.status.foreground_color
                              }
                              background_color={
                                capacityAllocation.task.status.background_color
                              }
                            />
                          )}
                          {showTimeOffBadge && (
                            <Center
                              h="8"
                              px="4"
                              py="1"
                              color="white"
                              fontFamily="heading"
                              fontSize="xs"
                              fontWeight="semibold"
                              bg="grey.500"
                              rounded="md"
                            >
                              {capacityAllocationItem?.type ===
                              CapacityAllocationTypeEnum.TimeOff
                                ? t`Time off`
                                : t`Home office`}
                            </Center>
                          )}
                          {showMeetingBadge && (
                            <Center
                              h="8"
                              px="4"
                              py="1"
                              color="grey.900"
                              fontFamily="heading"
                              fontSize="xs"
                              fontWeight="semibold"
                              bg="grey.100"
                              rounded="md"
                            >
                              <Trans>Meeting</Trans>
                            </Center>
                          )}
                          {showSpacer && <Box />}
                          <ButtonGroup colorScheme="grey" variant="ghost">
                            {(can("planning_update_all") ||
                              can({
                                do: "planning_update_own",
                                eval: ({ id }) =>
                                  id === capacityAllocation?.createdBy?.id ||
                                  id === capacityAllocationItem?.createdBy.id,
                              })) && (
                              // eslint-disable-next-line react/jsx-no-useless-fragment
                              <Fragment>
                                {userItem.isRepeating ? (
                                  <Menu>
                                    <Tooltip label={t`Edit`}>
                                      <MenuButton
                                        as={IconButton}
                                        aria-label={t`Edit allocation`}
                                        icon={<Icon name="edit-02" />}
                                      />
                                    </Tooltip>
                                    <MenuList>
                                      <MenuItem
                                        onClick={() => onEdit("currentItem")}
                                      >
                                        <Trans>This allocation</Trans>
                                      </MenuItem>
                                      {capacityAllocation?.repetition !==
                                        CapacityAllocationRepetitionTypeEnum.Once && (
                                        <MenuItem
                                          onClick={() => onEdit("thisDay")}
                                        >
                                          <Trans>This day (all users)</Trans>
                                        </MenuItem>
                                      )}
                                      <MenuItem
                                        onClick={() => onEdit("allOccurrences")}
                                      >
                                        <Trans>All occurrences</Trans>
                                      </MenuItem>
                                    </MenuList>
                                  </Menu>
                                ) : (
                                  <Tooltip label={t`Edit`}>
                                    <IconButton
                                      aria-label={t`Edit allocation`}
                                      icon={<Icon name="edit-02" />}
                                      onClick={() => onEdit("allOccurrences")}
                                    />
                                  </Tooltip>
                                )}
                              </Fragment>
                            )}
                            {(can("planning_delete_all") ||
                              can({
                                do: "planning_delete_own",
                                eval: ({ id }) =>
                                  id === capacityAllocation?.createdBy.id ||
                                  id === capacityAllocationItem?.createdBy.id,
                              })) && (
                              // eslint-disable-next-line react/jsx-no-useless-fragment
                              <Fragment>
                                {userItem.isRepeating ? (
                                  <Menu>
                                    <Tooltip label={t`Delete`}>
                                      <MenuButton
                                        as={IconButton}
                                        aria-label={t`Delete allocation`}
                                        icon={<Icon name="trash-03" />}
                                      />
                                    </Tooltip>
                                    {/* TODO onClicks */}
                                    <MenuList>
                                      <MenuItem
                                        onClick={() => onDelete("currentItem")}
                                      >
                                        <Trans>This allocation</Trans>
                                      </MenuItem>
                                      {capacityAllocation?.repetition !==
                                        CapacityAllocationRepetitionTypeEnum.Once && (
                                        <MenuItem
                                          onClick={() => onDelete("thisDay")}
                                        >
                                          <Trans>This day (all users)</Trans>
                                        </MenuItem>
                                      )}
                                      <MenuItem
                                        onClick={() =>
                                          onDelete("allOccurrences")
                                        }
                                      >
                                        <Trans>All occurrences</Trans>
                                      </MenuItem>
                                    </MenuList>
                                  </Menu>
                                ) : (
                                  <Tooltip label={t`Delete`}>
                                    <IconButton
                                      aria-label={t`Delete allocation`}
                                      icon={<Icon name="trash-03" />}
                                      onClick={() => onDelete("allOccurrences")}
                                    />
                                  </Tooltip>
                                )}
                              </Fragment>
                            )}
                            {can(
                              "planning_create_all",
                              "planning_create_own",
                            ) && (
                              // eslint-disable-next-line react/jsx-no-useless-fragment
                              <Fragment>
                                {userItem.isRepeating ? (
                                  <Menu>
                                    <Tooltip label={t`Duplicate`}>
                                      <MenuButton
                                        as={IconButton}
                                        aria-label={t`Duplicate allocation`}
                                        icon={<Icon name="copy-02" />}
                                      />
                                    </Tooltip>
                                    <MenuList>
                                      <MenuItem
                                        onClick={() =>
                                          onDuplicate("currentItem")
                                        }
                                      >
                                        <Trans>This allocation</Trans>
                                      </MenuItem>
                                      <MenuItem
                                        onClick={() =>
                                          onDuplicate("allOccurrences")
                                        }
                                      >
                                        <Trans>All occurrences</Trans>
                                      </MenuItem>
                                    </MenuList>
                                  </Menu>
                                ) : (
                                  <Tooltip label={t`Duplicate`}>
                                    <IconButton
                                      aria-label={t`Duplicate allocation`}
                                      icon={<Icon name="copy-02" />}
                                      onClick={() => onDuplicate("currentItem")}
                                    />
                                  </Tooltip>
                                )}
                              </Fragment>
                            )}
                            {capacityAllocationItem &&
                              capacityAllocationItem.type !==
                                CapacityAllocationTypeEnum.TimeOff &&
                              capacityAllocationItem.type !==
                                CapacityAllocationTypeEnum.HomeOffice && (
                                <Tooltip label={t`Open task`}>
                                  <IconButton
                                    aria-label={t`Open task`}
                                    icon={<Icon name="check-done-01" />}
                                    onClick={onOpenTask}
                                  />
                                </Tooltip>
                              )}
                            <Tooltip label={t`Close`}>
                              <IconButton
                                aria-label={t`Close`}
                                icon={<Icon name="x-close" />}
                                onClick={onClose}
                              />
                            </Tooltip>
                          </ButtonGroup>
                        </HStack>
                      )}
                    </PopoverHeader>
                    <PopoverBody px="4" py="3" w="0" minW="full">
                      <Stack spacing="2">
                        <Box>
                          {!!capacityAllocation?.task?.name && (
                            <Text
                              display="inline"
                              color="grey.700"
                              fontSize="2xs"
                              fontWeight="semibold"
                            >
                              {capacityAllocation.task.name}
                            </Text>
                          )}
                          {!!capacityAllocationItem?.timeOffType?.name && (
                            <Heading size="xs">
                              {capacityAllocationItem.timeOffType.name}
                            </Heading>
                          )}
                          {(!!capacityAllocation?.task?.id ||
                            !!capacityAllocation?.title) && (
                            <Heading size="xs">
                              {project ? (
                                `${project?.code} - ${project?.title}`
                              ) : capacityAllocation?.title ? (
                                capacityAllocation.title
                              ) : (
                                <Trans>Unassigned</Trans>
                              )}
                            </Heading>
                          )}
                          {!!capacityAllocation?.task?.id &&
                            project &&
                            budgetItem && (
                              <Link
                                flex="1"
                                fontSize="2xs"
                                href={{
                                  pathname: "/projects/detail",
                                  query: { id: project.id },
                                  hash: kebabCase(budgetItem.title),
                                }}
                                color="grey.500"
                                fontWeight="semibold"
                              >
                                {budgetItem.title}
                              </Link>
                            )}
                          {capacityAllocationItem?.type ===
                            CapacityAllocationTypeEnum.Imported && (
                            <Box
                              color="grey.500"
                              fontSize="2xs"
                              fontWeight="semibold"
                            >
                              <Icon
                                name="download-02"
                                w="12px"
                                h="12px"
                                mr="4px"
                              />
                              <Trans>Imported from your calendar</Trans>
                            </Box>
                          )}
                        </Box>
                        <Stack spacing="0">
                          <PropertyLabel>
                            <Trans>Assigned to</Trans>
                          </PropertyLabel>
                          <Text fontSize="xs">
                            {capacityAllocationItem?.user.full_name}
                          </Text>
                          {!!capacityAllocationItem?.global && (
                            <Text
                              color="grey.500"
                              fontSize="xs"
                              fontWeight="medium"
                            >
                              {t`+ all users from workspace`}
                            </Text>
                          )}
                          {!!capacityAllocationItem?.teams?.length && (
                            <Text
                              color="grey.500"
                              fontSize="xs"
                              fontWeight="medium"
                            >
                              {t`+ all users from ${capacityAllocationItem.teams
                                .map(({ team }) => team.name)
                                .join(", ")} ${plural(
                                capacityAllocationItem.teams.length,
                                {
                                  one: "team",
                                  other: "teams",
                                },
                              )}`}
                            </Text>
                          )}
                          {!capacityAllocationItem?.teams?.length &&
                            !capacityAllocationItem?.global &&
                            !!otherAssignees.length && (
                              <Text
                                color="grey.500"
                                fontSize="xs"
                                fontWeight="medium"
                              >
                                +{" "}
                                {otherAssignees
                                  .map((assignee) => assignee.user.full_name)
                                  .join(", ")}
                              </Text>
                            )}
                        </Stack>
                        <Box py="2">
                          <HStack
                            justify="space-around"
                            py="2"
                            borderColor="grey.100"
                            borderTopWidth="1px"
                            borderBottomWidth="1px"
                          >
                            <Stack align="baseline">
                              <PropertyLabel>
                                <Trans>Duration</Trans>
                              </PropertyLabel>
                              <Stack>
                                <Text fontSize="xs">
                                  {capacityAllocationItem?.from_time
                                    ? `${capacityAllocationItem?.from_time} - ${capacityAllocationItem?.to_time}`
                                    : `${formatDuration(
                                        capacityAllocationItem?.total_time ?? 0,
                                        {
                                          noPadStart: true,
                                        },
                                      )}`}
                                </Text>
                                {/* WE don't want to show repetition info in popover but there isn't other place where to show it yet */}
                                {/* {timeOff?.repetition && timeOff.repetition_settings && (
                    <RepetitionInfo
                    repetition={timeOff.repetition}
                    repetitionSettings={timeOff.repetition_settings}
                    />
                    )}
                    {capacityAllocation?.repetition && capacityAllocation.repetition_settings && (
                      <RepetitionInfo
                      repetition={capacityAllocation.repetition}
                      repetitionSettings={capacityAllocation.repetition_settings}
                      />
                    )} */}
                              </Stack>
                            </Stack>
                            {!!capacityAllocation?.task?.id && (
                              <Stack>
                                <PropertyLabel>
                                  <Trans>Tracked / Planned</Trans>
                                </PropertyLabel>
                                <HStack>
                                  <TrackedTimeIcon
                                    color={`${
                                      capacityAllocationItem?.userStats
                                        ?.has_running_timer
                                        ? "green"
                                        : "grey"
                                    }.600`}
                                  />
                                  <Text
                                    color={`${
                                      capacityAllocationItem?.userStats
                                        ?.has_running_timer
                                        ? "green"
                                        : "grey"
                                    }.700`}
                                    fontSize="xs"
                                    fontWeight="semibold"
                                  >
                                    {formatDuration(
                                      capacityAllocationItem?.userStats
                                        ?.tracked_time_for_item ?? 0,
                                      {
                                        noPadStart: true,
                                      },
                                    )}
                                  </Text>{" "}
                                  <Text fontSize="xs">|</Text>{" "}
                                  <Text fontSize="xs" fontWeight="semibold">
                                    {formatDuration(
                                      capacityAllocationItem?.total_time ?? 0,
                                      {
                                        noPadStart: true,
                                        roundSeconds: true,
                                      },
                                    )}
                                  </Text>
                                </HStack>
                              </Stack>
                            )}
                          </HStack>
                        </Box>

                        {!!capacityAllocation?.deadline && (
                          <Stack>
                            <PropertyLabel>
                              <Trans>Deadline</Trans>
                            </PropertyLabel>

                            <WrapComponent
                              if={
                                highlightDeadline(
                                  capacityAllocation.deadline,
                                ) && !!capacityAllocationItem?.completed
                              }
                              with={(children) => (
                                <Box>
                                  <Tag colorScheme="red">
                                    {cloneElement(children, {
                                      fontWeight: "medium",
                                    })}
                                  </Tag>
                                </Box>
                              )}
                            >
                              <Text fontSize="xs">
                                {`${
                                  capacityAllocation?.deadline_time
                                    ? `${capacityAllocation?.deadline_time}, `
                                    : ""
                                }${
                                  capacityAllocation?.deadline
                                    ? format(
                                        capacityAllocation?.deadline,
                                        "d MMMM y",
                                      )
                                    : ""
                                }`}
                              </Text>
                            </WrapComponent>
                          </Stack>
                        )}

                        {!!capacityAllocation?.location && (
                          <Stack>
                            <PropertyLabel>
                              <Trans>Location</Trans>
                            </PropertyLabel>
                            <TextEditor
                              maxW="400px"
                              initialValue={capacityAllocation.location}
                              onChange={() => {}}
                              isEditable={false}
                              attachments={[]}
                            />
                          </Stack>
                        )}

                        {!!capacityAllocationItem?.note && (
                          <Stack>
                            <PropertyLabel>
                              <Trans>Note</Trans>
                            </PropertyLabel>
                            <NoteWithLinks
                              note={capacityAllocationItem.note}
                              onLinkClick={handleNoteLinkClick}
                            />
                          </Stack>
                        )}

                        {!!capacityAllocation?.calendarParticipants?.length && (
                          <Stack spacing="0">
                            <PropertyLabel>
                              <Trans>Calendar participants</Trans>
                            </PropertyLabel>
                            {capacityAllocation.calendarParticipants.map(
                              (participant) => (
                                <Text
                                  key={participant.id}
                                  color="grey.700"
                                  fontSize="xs"
                                >
                                  {participant.display_name ??
                                    participant.email}
                                </Text>
                              ),
                            )}
                          </Stack>
                        )}

                        {(capacityAllocationItem?.meetLink ||
                          capacityAllocationItem?.eventLink) && (
                          <HStack w="full">
                            {capacityAllocationItem?.meetLink && (
                              <ButtonLink
                                href={capacityAllocationItem.meetLink}
                                isExternal
                                size="sm"
                                variant="subtle"
                              >
                                <Trans>Join meeting</Trans>
                              </ButtonLink>
                            )}

                            {capacityAllocationItem?.eventLink && (
                              <ButtonLink
                                href={capacityAllocationItem.eventLink}
                                isExternal
                                variant="subtle"
                                size="sm"
                              >
                                <Trans>Event</Trans>
                              </ButtonLink>
                            )}
                          </HStack>
                        )}

                        {capacityAllocation?.task?.id && (
                          <ButtonGroup spacing="2">
                            {isAuthUser && (
                              <TimeTrackingButton
                                onTimeTrackingChange={onTimeTrackingChange}
                                taskId={capacityAllocation.task.id}
                                timeTrackingSettings={{
                                  tracking_enabled:
                                    caiStats?.tracking_enabled ?? false,
                                  tracking_enabled_from:
                                    caiStats?.tracking_enabled_from,
                                  tracking_enabled_to:
                                    caiStats?.tracking_enabled_to,
                                }}
                                capacityAllocationItemId={
                                  capacityAllocationItem?.id
                                }
                                trackForUser={capacityAllocationItem?.user.id}
                                trackForDate={capacityAllocationItem?.date}
                                trackForPosition={
                                  capacityAllocationItem?.time_tracking_work_type_id ??
                                  undefined
                                }
                                note={capacityAllocationItem?.note}
                              />
                            )}
                            {(can("planning_complete_all") ||
                              can({
                                do: "planning_complete_own",
                                eval: ({ id }) =>
                                  id === capacityAllocationItem?.user.id,
                              })) && (
                              <Fragment>
                                {!!capacityAllocationItem &&
                                capacityAllocationItem?.id &&
                                capacityAllocationItem?.completed ? (
                                  <Button
                                    colorScheme="grey"
                                    isLoading={isReopenButtonLoading}
                                    leftIcon={<Icon name="flip-backward" />}
                                    onClick={onReopen}
                                    variant="outline"
                                  >
                                    <Trans>Reopen</Trans>
                                  </Button>
                                ) : (
                                  <Button
                                    colorScheme="grey"
                                    isDisabled={
                                      caiStats
                                        ? !isTrackingEnabled(
                                            caiStats,
                                            capacityAllocationItem?.date,
                                          )
                                        : false
                                    }
                                    leftIcon={<Icon name="check" />}
                                    onClick={onComplete}
                                    variant="outline"
                                  >
                                    <Trans>Complete</Trans>
                                  </Button>
                                )}
                              </Fragment>
                            )}
                          </ButtonGroup>
                        )}
                      </Stack>
                    </PopoverBody>
                    <PopoverFooter px="4" w="0" minW="full">
                      <HStack justify="space-between">
                        <HStack align="center" spacing="2">
                          <Text fontSize="2xs">
                            <Trans>Created by</Trans>{" "}
                            <b>{capacityAllocationItem?.createdBy.full_name}</b>
                          </Text>
                        </HStack>
                        {capacityAllocation?.created_at && (
                          <Text fontSize="2xs">
                            {format(
                              new Date(capacityAllocation.created_at),
                              "dd.MM.yyyy, H:mm",
                            )}
                          </Text>
                        )}
                      </HStack>
                    </PopoverFooter>
                  </Fragment>
                )}
              </PopoverContent>
            </Portal>
          </Popover>
          <ExternalLinkModal
            url={externalLinkUrl}
            isOpen={isExternalLinkOpen}
            onClose={onExternalLinkClose}
          />
        </>
      );
    },
  ),
);
